import React, { useState, useEffect } from 'react';
import { Modal, LoadingOverlay, Stack, Title, Button, Group, ActionIcon, Text } from '@mantine/core';
import './AddEmployeesPage.css';
import EmployeeForm from './forms/EmployeeForm';
import { IconTrash, IconChevronRight, IconPlus } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useSalon } from '../context/SalonContext';
import { useMediaQuery } from '@mantine/hooks';

function AddEmployeesPage({ onSubmit }) {
  const { deleteStaff } = useApp();
  const { businessInfo, employees, setEmployees, currentUser } = useSalon();
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const isMobile = useMediaQuery('(max-width: 768px)');
  useEffect(() => {
    if (businessInfo && employees) {
      setIsLoading(false);
    }
  }, [businessInfo, currentUser]);

  const handleAddEmployee = (newEmployee) => {
    setEmployees([...employees, newEmployee]);
  };

  const handleEditEmployee = (employeeData) => {
    setEmployees(employees.map(emp => 
      emp.id === selectedEmployee.id ? { ...emp, ...employeeData } : emp
    ));
  };

  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    setIsEditFormOpen(true);
  };

  const handleDeleteEmployee = async (id, e) => {
    setIsLoading(true);
    e.stopPropagation(); // Prevent modal from opening when deleting
    await deleteStaff(id).then(() => {
      setEmployees(employees.filter(emp => emp.id !== id));
      setIsLoading(false);
    });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    onSubmit(employees);
  };

  const handleSkip = () => {
    onSubmit(employees);
  };

  return (
    <Stack>
      <LoadingOverlay visible={isLoading} />
      <Stack p={{base: "0", sm: "md", md: "xl"}} w={isMobile ? "100%" : "90%"} maw="60rem" mx="auto" align="center">
        <Title order={2} c="gray.8">Add Employees</Title>
        
        <Stack w="100%" mb="xl" gap="0">
          {employees && employees.map((employee) => (
            <Group 
              p="lg"
              key={employee.id} 
              className="employee-row"
              justify="space-between"
              onClick={() => handleEmployeeClick(employee)}
              style={{ cursor: "pointer", borderBottom: "1px solid #e8e8e8" }}
            >
                <Group>
                    {!employee.isSelf && (
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={(e) => handleDeleteEmployee(employee.id, e)}
                        size={isMobile ? "sm" : "lg"}
                        radius="xl"
                        aria-label="Delete employee"
                      >
                        <IconTrash />
                      </ActionIcon>
                    )}
                    <Text c="gray.8" size={isMobile ? "sm" : "md"}>{employee.first_name} {employee.last_name}</Text>
                </Group>
                <Group>
                    <Text c="gray.6" size={isMobile ? "sm" : "md"}>{employee.role}{employee.title ? ` | ${employee.title}` : ''}</Text>
                    <IconChevronRight size={isMobile ? 16 : 20} />
                </Group>
            </Group>
          ))}
          
          <Button
            onClick={() => setIsFormOpen(true)}
            leftSection={<IconPlus size={20} />}
            variant="subtle"
            size="md"
            radius="xl"
            color="violet"
            py="sm"
            h="fit-content"
          >
            Add Employee
          </Button>
        </Stack>

        <Stack w="100%" px={isMobile ? "md" : "xl"}>
          <Button type="submit" fullWidth onClick={handleContinue} size={isMobile ? "sm" : "md"} color="violet">
            Continue
          </Button>
          <Button variant="light" onClick={handleSkip} fullWidth size={isMobile ? "sm" : "md"} color="gray">
            Skip for Now
          </Button>
        </Stack>

        {/* Add Employee Modal */}
        <Modal
          opened={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          title=""
          size="lg"
        >
          <EmployeeForm
            onSubmit={handleAddEmployee}
            onClose={() => setIsFormOpen(false)}
            businessInfo={businessInfo}
          />
        </Modal>

        {/* Edit Employee Modal */}
        <Modal
          opened={isEditFormOpen}
          onClose={() => {
            setIsEditFormOpen(false);
            setSelectedEmployee(null);
          }}
          title=""
          size="lg"
        >
          <EmployeeForm
            initialData={selectedEmployee}
            onSubmit={handleEditEmployee}
            onClose={() => {
              setIsEditFormOpen(false);
              setSelectedEmployee(null);
            }}
            businessInfo={businessInfo}
          />
        </Modal>
      </Stack>
    </Stack>
  );
}

export default AddEmployeesPage; 